import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Plus, Search, Filter, MapPin, Clock, User,
  Briefcase, ChevronRight, ChevronLeft,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext.js';
import { onsiteApiFetch } from '../config/api.js';
import { formatDate, formatTime } from '../lib/utils.js';
import type { OnsiteJob, JobStatus, JobType, JobPriority } from '../../../api/src/types/onsite-types.js';

const PAGE_SIZE = 50;

const STATUS_CONFIG: Record<string, { label: string; color: string; dot: string }> = {
  new:        { label: 'New',        color: 'bg-purple-500/20 text-purple-400', dot: 'bg-purple-500'  },
  scheduled:  { label: 'Scheduled',  color: 'bg-blue-500/20 text-blue-400',    dot: 'bg-blue-500'    },
  dispatched: { label: 'Dispatched', color: 'bg-cyan-500/20 text-cyan-400',    dot: 'bg-cyan-500'    },
  en_route:   { label: 'En Route',   color: 'bg-amber-500/20 text-amber-400',  dot: 'bg-amber-500'   },
  on_site:    { label: 'On Site',    color: 'bg-green-500/20 text-green-400',  dot: 'bg-green-500'   },
  completed:  { label: 'Completed',  color: 'bg-muted/20 text-muted-foreground',  dot: 'bg-muted-foreground'   },
  invoiced:   { label: 'Invoiced',   color: 'bg-emerald-500/20 text-emerald-400', dot: 'bg-emerald-500' },
  closed:     { label: 'Closed',     color: 'bg-muted/20 text-muted-foreground',  dot: 'bg-muted-foreground'   },
  cancelled:  { label: 'Cancelled',  color: 'bg-red-500/20 text-red-400',      dot: 'bg-red-500'     },
};

const PRIORITY_BADGE: Record<string, string> = {
  low:    'bg-muted text-foreground',
  normal: 'bg-muted text-foreground',
  high:   'bg-amber-500/20 text-amber-400',
  urgent: 'bg-red-500/20 text-red-400',
};

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All Statuses' },
  { value: 'new', label: 'New' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'dispatched', label: 'Dispatched' },
  { value: 'en_route', label: 'En Route' },
  { value: 'on_site', label: 'On Site' },
  { value: 'completed', label: 'Completed' },
  { value: 'invoiced', label: 'Invoiced' },
  { value: 'closed', label: 'Closed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All Types' },
  { value: 'installation', label: 'Installation' },
  { value: 'repair', label: 'Repair' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'break_fix', label: 'Break/Fix' },
  { value: 'project', label: 'Project' },
  { value: 'assessment', label: 'Assessment' },
  { value: 'other', label: 'Other' },
];

export default function JobsListPage() {
  const { orgId, isDispatcher } = useAuth();
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [specialFilter, setSpecialFilter] = useState<'' | 'overdue' | 'unscheduled'>('');
  const [page, setPage] = useState(0);

  // Sync special filter from URL (?filter=overdue|unscheduled).
  useEffect(() => {
    const f = searchParams.get('filter');
    if (f === 'overdue' || f === 'unscheduled') {
      setSpecialFilter(f);
      setShowFilters(true);
    } else {
      setSpecialFilter('');
    }
  }, [searchParams]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [search, statusFilter, typeFilter, specialFilter]);

  const params = new URLSearchParams();
  if (search) params.set('q', search);
  if (statusFilter) params.set('status', statusFilter);
  if (typeFilter) params.set('job_type', typeFilter);
  params.set('limit', PAGE_SIZE.toString());
  params.set('offset', (page * PAGE_SIZE).toString());

  // Map special filters to the dedicated API endpoints.
  const endpoint = specialFilter === 'overdue'
    ? `/api/v1/onsite/jobs/overdue`
    : specialFilter === 'unscheduled'
      ? `/api/v1/onsite/jobs/unscheduled`
      : `/api/v1/onsite/jobs?${params.toString()}`;

  const { data, isLoading, error } = useQuery({
    queryKey: ['jobs', 'list', orgId, search, statusFilter, typeFilter, specialFilter, page],
    queryFn: () => onsiteApiFetch<{ jobs: OnsiteJob[]; total?: number; limit?: number; offset?: number }>(endpoint),
    enabled: !!orgId,
  });

  const jobs = data?.jobs ?? [];
  const total = data?.total ?? jobs.length;
  const hasNext = !specialFilter && total > (page + 1) * PAGE_SIZE;
  const hasPrev = page > 0;

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b border-border bg-card p-4">
        <div className="mb-3 flex items-center justify-between">
          <h1 className="text-xl font-bold text-foreground">Jobs</h1>
          <Link
            to="/jobs/new"
            className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" />
            New Job
          </Link>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search jobs, clients, ticket numbers..."
            className="w-full rounded-lg border border-border bg-secondary py-2.5 pl-10 pr-10 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none"
          />
          <button
            onClick={() => setShowFilters(f => !f)}
            className={`absolute right-2 top-1/2 -translate-y-1/2 rounded p-1.5 ${showFilters ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}
          >
            <Filter className="h-4 w-4" />
          </button>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="mt-3 flex gap-3">
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none"
            >
              {STATUS_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <select
              value={typeFilter}
              onChange={e => setTypeFilter(e.target.value)}
              className="rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none"
            >
              {TYPE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Special filter banner */}
      {specialFilter && (
        <div className="border-b border-border bg-amber-500/10 px-4 py-2 text-xs text-amber-300 flex items-center justify-between">
          <span>
            Filtered: <strong className="capitalize">{specialFilter}</strong> jobs
          </span>
          <Link to="/jobs" className="text-amber-200 hover:underline">Clear filter</Link>
        </div>
      )}

      {/* Jobs list */}
      <div className="flex-1 overflow-y-auto">
        {error ? (
          <div className="flex flex-col items-center justify-center gap-3 py-20 text-muted-foreground">
            <Briefcase className="h-10 w-10" />
            <p className="font-medium text-foreground">Couldn't load jobs</p>
            <p className="text-sm text-red-400">{(error as Error).message}</p>
          </div>
        ) : isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        ) : jobs.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 py-20 text-muted-foreground">
            <Briefcase className="h-12 w-12" />
            <p className="text-lg font-medium">No jobs found</p>
            <p className="text-sm text-center max-w-xs">
              {search || statusFilter || typeFilter
                ? 'Try adjusting your filters'
                : 'Create your first job to get started'}
            </p>
            {!search && !statusFilter && !typeFilter && (
              <Link
                to="/jobs/new"
                className="mt-2 flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-medium text-foreground hover:bg-primary/90"
              >
                <Plus className="h-4 w-4" />
                Create Job
              </Link>
            )}
          </div>
        ) : (
          <>
          <div className="divide-y divide-border">
            {jobs.map(job => {
              const statusCfg = STATUS_CONFIG[job.status] ?? STATUS_CONFIG.new;
              return (
                <Link
                  key={job.id}
                  to={`/jobs/${job.id}`}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-secondary/50 transition-colors"
                >
                  <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${statusCfg.dot}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="text-sm font-medium text-foreground line-clamp-1">{job.title}</p>
                      {job.priority !== 'normal' && (
                        <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-xs ${PRIORITY_BADGE[job.priority]}`}>
                          {job.priority}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      {job.customer_name && (
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {job.customer_name}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDate(job.scheduled_start)} {formatTime(job.scheduled_start)}
                      </span>
                      {job.assigned_tech_name && (
                        <span className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {job.assigned_tech_name}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${statusCfg.color}`}>
                    {statusCfg.label}
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                </Link>
              );
            })}
          </div>

          {/* Pagination */}
          {!specialFilter && (hasPrev || hasNext) && (
            <div className="flex items-center justify-between border-t border-border bg-card px-4 py-3">
              <p className="text-xs text-muted-foreground">
                Page {page + 1} · {jobs.length} of {total}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={!hasPrev}
                  className="flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-xs text-muted-foreground hover:bg-secondary hover:text-foreground disabled:opacity-50"
                >
                  <ChevronLeft className="h-3.5 w-3.5" /> Prev
                </button>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={!hasNext}
                  className="flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-xs text-muted-foreground hover:bg-secondary hover:text-foreground disabled:opacity-50"
                >
                  Next <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}
          </>
        )}
      </div>
    </div>
  );
}
